import PromiseQueue from '@dabble/data/collective/promise-queue';
import { statefulPromise, StatefulPromise } from '@dabble/data/collective/stateful-promise';
import rest from '@dabble/data/rest';
import { countWords } from '@dabble/data/word-count';
import { DictionaryEntry, Issue, IssueCategory, Language } from '../grammar-types';

const UILanguages = ['en-US', 'es', 'es-ES'];
const MAX_SUGGESTION_LENGTH = 5;

export default class BeyondGrammarApi {
  static languages: Language[];
  static uiLocales: Language[];
  languageIsoCode: string;
  spellingCheck: boolean;
  grammarCheck: boolean;
  queue: PromiseQueue;

  constructor() {
    this.languageIsoCode = 'en-US';
    this.spellingCheck = true;
    this.grammarCheck = true;
    this.queue = new PromiseQueue(4, Infinity);
  }

  setLanguage(languageIsoCode: string): void {
    this.languageIsoCode = languageIsoCode;
  }

  check(text: string, runFirst = false): StatefulPromise<Issue[]> {
    if (!text || !text.trim() || countWords(text, 2) < 2) {
      return statefulPromise<Issue[]>().resolve([]);
    }
    const options = {
      text,
      language: this.languageIsoCode,
      includeSpelling: this.spellingCheck,
      includeGrammar: this.grammarCheck,
      includeStyle: this.grammarCheck,
      heavyGrammar: this.grammarCheck,
    };

    const checker: any = async (count = 0) => {
      let result;
      try {
        result = await rest.POST('grammar/check', { options });
      } catch (e) {
        if (++count === 3) throw new Error('Unable to check this paragraph');
        return await checker(count);
      }

      let issues = result?.tags as Issue[];

      if (issues) {
        issues = issues.map(issue => {
          let { category, hint, ruleId, suggestions, startPos, endPos } = issue;
          endPos += 1;
          category = category.toLowerCase() as IssueCategory;
          if (suggestions && suggestions.length > MAX_SUGGESTION_LENGTH) suggestions.length = MAX_SUGGESTION_LENGTH;
          suggestions = suggestions.map(suggestion => (suggestion === '(omit)' ? '' : suggestion));
          issue = { category, hint, ruleId, suggestions, startPos, endPos };
          const word = text.slice(startPos, endPos);
          if (category === 'spelling') {
            issue.word = word;
          }
          return issue;
        });
      }

      return issues;
    };

    return runFirst ? this.queue.addToHead(checker) : this.queue.add(checker);
  }

  cancelPendingRequests() {
    this.queue.clear();
  }

  async getLanguages() {
    if (!BeyondGrammarApi.languages || !BeyondGrammarApi.languages.length) {
      BeyondGrammarApi.languages = await rest.GET('grammar/languages');
    }
    return BeyondGrammarApi.languages;
  }

  async getUILocales() {
    if (!BeyondGrammarApi.uiLocales || !BeyondGrammarApi.uiLocales.length) {
      const uiLanguages = await rest.GET('grammar/languages');
      BeyondGrammarApi.uiLocales = uiLanguages.filter((l: any) => {
        return UILanguages.includes(l.languageCode);
      });
    }
    return BeyondGrammarApi.uiLocales;
  }

  async thesaurus(word: string) {
    const query = `?language=${this.languageIsoCode}&word=${encodeURIComponent(word)}`;
    return await rest.GET(`grammar/thesaurus${query}`);
  }

  async contextualThesaurus(text: string, start: number, end: number) {
    const query =
      `language=${this.languageIsoCode}` + `&text=${encodeURIComponent(text)}` + `&start=${start}&end=${end}`;
    return await rest.GET(`grammar/contextual-thesaurus?${query}`);
  }

  async addToDictionary(word: string, replace?: string) {
    return await rest.POST('grammar/dictionary', { language: this.languageIsoCode, word, replace });
  }

  async removeFromDictionary(id: string) {
    return await rest.DELETE(`grammar/dictionary/${id}`);
  }

  async getDictionaryEntries(): Promise<DictionaryEntry[]> {
    const language = this.languageIsoCode;
    return await rest.GET(`grammar/dictionary?language=${language}`);
  }

  async getHelpText(ruleId: string, text: string, suggestions: string) {
    const query = `?ruleId=${ruleId}&text=${text}&suggestions=${suggestions}`;
    return await rest.GET(`grammar/help${query}`);
  }

  queuePendingSize() {
    return this.queue.getPendingLength();
  }
}
